import type Game from '../../../scenes/Game';
import { virtualRoomAreaService } from '../../../services/VirtualRoomAreaService';
import { getPlayerPositionForMediaProximity } from '../../../services/getPlayerPositionForMediaProximity';
import type { VirtualRoomFilterEntry } from '../../../stores/VirtualRoomFilterStore';
import { resolveConferenceMediaEligibility } from './resolveConferenceMediaEligibility';

export interface SameVirtualRoomMediaResult {
  sameRoomMedia: boolean;
  filterRoomId: string | null;
  passwordOk: boolean;
  shouldSubscribeMedia: boolean;
  serverSameCell: boolean;
  clientSameVirtualRoom: boolean;
}

export function getLocalServerVirtualRoomId(game: Game): string | null {
  const mySid = game.network?.mySessionId || '';
  const colyseusRoom = game.network?.room;
  const schemaMe = mySid
    ? (colyseusRoom?.state?.players?.get(mySid) as { serverVirtualRoomId?: string } | undefined)
    : undefined;
  return schemaMe?.serverVirtualRoomId && schemaMe.serverVirtualRoomId.length > 0
    ? schemaMe.serverVirtualRoomId
    : null;
}

/**
 * Whether the LOCAL player is authorized for the room they are currently inside, per the
 * server-authoritative `virtualRoomAuthorized` schema flag. Returns true when not inside any
 * room, when the flag is absent (older server — no regression), or when authorized; false only
 * when the server says the local player is physically inside a locked room they have not entered
 * the password for. Used to hard-block the returner's OWN outbound subscriptions after a
 * leave→rejoin, independent of VIRTUAL_ROOM_FILTER_STATE message timing. [SC-246]
 */
export function isLocalPlayerVirtualRoomAuthorized(game: Game | null | undefined): boolean {
  const mySid = game?.network?.mySessionId || '';
  const colyseusRoom = game?.network?.room;
  const schemaMe = mySid
    ? (colyseusRoom?.state?.players?.get(mySid) as
        | { serverVirtualRoomId?: string; virtualRoomAuthorized?: boolean }
        | undefined)
    : undefined;
  if (!schemaMe) return true;
  const inRoom = !!(schemaMe.serverVirtualRoomId && schemaMe.serverVirtualRoomId.length > 0);
  if (!inRoom) return true;
  return schemaMe.virtualRoomAuthorized !== false;
}

/** Prefer broadcast VR id (AoI-safe) over schema when available. */
export function getRemoteServerVirtualRoomId(game: Game, participantIdentity: string): string | null {
  const broadcastVr = game.network?.getBroadcastVirtualRoomId?.(participantIdentity) || null;
  const colyseusRoom = game.network?.room;
  const schemaOther = colyseusRoom?.state?.players?.get(participantIdentity) as
    | { serverVirtualRoomId?: string }
    | undefined;
  const schemaVr =
    schemaOther?.serverVirtualRoomId && schemaOther.serverVirtualRoomId.length > 0
      ? schemaOther.serverVirtualRoomId
      : null;
  return broadcastVr || schemaVr;
}

export function isPasswordOkForVirtualRoomPair(
  filterRoomId: string,
  mySid: string,
  otherSid: string,
  virtualRoomFilters: Record<string, VirtualRoomFilterEntry>
): boolean {
  const roomFilter = virtualRoomFilters[filterRoomId];
  if (!roomFilter?.hasPassword && !roomFilter?.hasMeetingLock) return true;
  return (
    roomFilter.authorizedSessionIds.includes(mySid) &&
    roomFilter.authorizedSessionIds.includes(otherSid)
  );
}

/**
 * Server-VR isolation predicate: whether to hard-exclude a peer purely on the server-assigned
 * virtual-room ids, BEFORE consulting client-side area detection.
 *
 * Exclude only on a DEFINITIVE mismatch — i.e. when OUR OWN server VR is known and differs
 * from theirs (covers "different rooms" and "I'm inside a room, they're outside", the SC-256
 * isolation cases). When our own server VR is not yet assigned (`null`) — a fresh join, before
 * the server syncs our VR id back — do NOT hard-exclude here; defer to the password-gated
 * client-side area detection so a new joiner isn't starved of inbound media for the first
 * minutes (SC-273 AC3). The fall-through path still enforces the room password, so this cannot
 * leak a locked room.
 */
export function shouldExcludeByServerVirtualRoom(
  myServerVr: string | null,
  otherServerVr: string | null
): boolean {
  return myServerVr != null && myServerVr !== otherServerVr;
}

/** Server/broadcast VR co-occupancy with password gates — no position required. */
export function hasServerVirtualRoomCoOccupancy(
  game: Game,
  participantIdentity: string,
  virtualRoomFilters: Record<string, VirtualRoomFilterEntry>
): boolean {
  const myServerVr = getLocalServerVirtualRoomId(game);
  if (!myServerVr) return false;
  const otherServerVr = getRemoteServerVirtualRoomId(game, participantIdentity);
  if (myServerVr !== otherServerVr) return false;
  const mySid = game.network?.mySessionId || '';
  return isPasswordOkForVirtualRoomPair(myServerVr, mySid, participantIdentity, virtualRoomFilters);
}

/**
 * Whether an in-range participant is someone the local player is really talking
 * to, rather than one `useProximityState` keeps "in range" only by its
 * no-position settle grace (which restarts whenever a player leaves this
 * client's state view, so far-away players flicker in large offices): their
 * position is known, or the server places them in our virtual room. Raise hand
 * counts only these as company (SC-415).
 */
export function isSettledMediaPeer(
  game: Game | null | undefined,
  participantIdentity: string,
  virtualRoomFilters: Record<string, VirtualRoomFilterEntry>
): boolean {
  if (!game) return false;
  return (
    !!getPlayerPositionForMediaProximity(game, participantIdentity) ||
    hasServerVirtualRoomCoOccupancy(game, participantIdentity, virtualRoomFilters)
  );
}

export function resolveSameVirtualRoomMedia(
  game: Game | null | undefined,
  participantIdentity: string,
  virtualRoomFilters: Record<string, VirtualRoomFilterEntry>
): SameVirtualRoomMediaResult {
  const empty: SameVirtualRoomMediaResult = {
    sameRoomMedia: false,
    filterRoomId: null,
    passwordOk: false,
    shouldSubscribeMedia: false,
    serverSameCell: false,
    clientSameVirtualRoom: false,
  };

  if (!game?.myPlayer) return empty;

  const myPlayer = game.myPlayer;
  const mySid = game.network?.mySessionId || '';
  const myServerVr = getLocalServerVirtualRoomId(game);
  const otherServerVr = getRemoteServerVirtualRoomId(game, participantIdentity);
  const serverSameCell = !!(myServerVr && otherServerVr && myServerVr === otherServerVr);

  const myArea = virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y);
  const myRoomId = myArea?.roomId || null;

  const otherPos = getPlayerPositionForMediaProximity(game, participantIdentity);
  const otherArea = otherPos
    ? virtualRoomAreaService.isPlayerInArea(otherPos.x, otherPos.y)
    : null;
  const clientSameVirtualRoom = !!(myArea && otherArea && myArea.roomId === otherArea.roomId);

  let sameRoomMedia = false;
  let filterRoomId: string | null = null;

  if (serverSameCell && myServerVr) {
    sameRoomMedia = true;
    filterRoomId = myServerVr;
  } else if (clientSameVirtualRoom && myRoomId) {
    sameRoomMedia = true;
    filterRoomId = myRoomId;
  }

  const passwordOk = filterRoomId
    ? isPasswordOkForVirtualRoomPair(filterRoomId, mySid, participantIdentity, virtualRoomFilters)
    : false;

  return {
    sameRoomMedia,
    filterRoomId,
    passwordOk,
    shouldSubscribeMedia: sameRoomMedia && passwordOk,
    serverSameCell,
    clientSameVirtualRoom,
  };
}

/** True when client area/position data says peers must not share media. */
export function shouldExcludeCrossRoomByPosition(
  game: Game,
  participantIdentity: string
): boolean {
  const myPlayer = game.myPlayer;
  if (!myPlayer) return true;

  const myArea = virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y);
  const myRoomId = myArea?.roomId || null;
  const otherPos = getPlayerPositionForMediaProximity(game, participantIdentity);
  if (!otherPos) return false;

  const otherArea = virtualRoomAreaService.isPlayerInArea(otherPos.x, otherPos.y);
  const otherRoomId = otherArea?.roomId || null;

  if (myRoomId !== null && otherRoomId !== null && myRoomId !== otherRoomId) return true;
  if ((myRoomId !== null && otherRoomId === null) || (myRoomId === null && otherRoomId !== null)) {
    return true;
  }
  return false;
}

/** Authoritative subscribe decision for a remote participant's media tracks. */
export function resolveMediaSubscriptionForParticipant(
  game: Game,
  participantIdentity: string,
  inProximity: boolean,
  virtualRoomFilters: Record<string, VirtualRoomFilterEntry>,
  options?: { isScreenSharing?: boolean }
): boolean {
  // A returner physically inside a locked room they have not re-entered the password for must not
  // subscribe to anyone until authorized — server-authoritative, immune to filter-broadcast races. [SC-246]
  if (!isLocalPlayerVirtualRoomAuthorized(game)) return false;

  if (
    resolveConferenceMediaEligibility(game, participantIdentity, virtualRoomFilters, {
      isScreenSharing: options?.isScreenSharing,
    })
  ) {
    return true;
  }

  const myPlayer = game.myPlayer;
  if (!myPlayer) return false;

  const otherPos = getPlayerPositionForMediaProximity(game, participantIdentity);
  if (!otherPos) return false;

  // Resolve areas once for both cross-room exclusion and password gate.
  const myArea = virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y);
  const myRoomId = myArea?.roomId || null;
  const otherArea = virtualRoomAreaService.isPlayerInArea(otherPos.x, otherPos.y);
  const otherRoomId = otherArea?.roomId || null;

  // Cross-room exclusion: block media when players are in different rooms
  // or when exactly one is inside a room boundary.
  const myInRoom = myRoomId !== null;
  const otherInRoom = otherRoomId !== null;
  if (myInRoom && otherInRoom && myRoomId !== otherRoomId) return false;
  if (myInRoom !== otherInRoom) return false;

  // Password gate: if either player is in a password-protected room,
  // both must be authorized before proximity audio is allowed.
  const roomId = myRoomId || otherRoomId;
  if (roomId) {
    const mySid = game.network?.mySessionId || '';
    if (!isPasswordOkForVirtualRoomPair(roomId, mySid, participantIdentity, virtualRoomFilters)) {
      return false;
    }
  }

  return inProximity;
}
